import { useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import type { HeroContent, Stat } from '~/content/types';
import { Section } from '~/components/layout';
import { ButtonLink, SmartImage } from '~/components/ui';
import { cn } from '~/lib/cn';
import { StatsRow } from './TrustBar';
import { CurrencyCalculator } from './CurrencyCalculator';

const MASK_MS = 800;
const DROP_PAUSE_MS = 196;
const DROP_MS = 956;
const CONTENT_MS = 490;
const CONTENT_DELAY_MS = MASK_MS + DROP_PAUSE_MS + 105;
const CARD_LEAD_MS = Math.round(MASK_MS * 0.5);
const MASK_INSET = 44;
const MEDIA_LIFT = 200;
type LoadPhase = 'pending' | 'animating' | 'settled' | 'done';

/**
 * Centered headline + CTA + stats row + full-bleed image (Figma Accounts hero).
 *
 * A fixed-size spacer reserves the image's final layout from the first paint.
 * While loading, the image is positioned over the stage and transforms into
 * that reserved slot, so hydration never changes its visible starting point.
 */
export function Hero({
  content,
  stats,
  animateOnLoad = true,
  showCalculator = false,
  statsSlot,
  statsClassName,
}: {
  content: HeroContent;
  stats?: Stat[];
  animateOnLoad?: boolean;
  /** Overlay the currency calculator on the image (Accounts hero). */
  showCalculator?: boolean;
  /** Replaces the default stats row in the hero band (e.g. the Stablecoin providers strip). */
  statsSlot?: ReactNode;
  /** Optional page-level spacing override for the stats band. */
  statsClassName?: string;
}) {
  const stageRef = useRef<HTMLDivElement>(null);
  const mediaSlotRef = useRef<HTMLDivElement>(null);
  const mediaRef = useRef<HTMLDivElement>(null);
  const [phase, setPhase] = useState<LoadPhase>(() => (animateOnLoad ? 'pending' : 'done'));
  // Deferring the calculator's mount (rather than just its CSS animation) means its own
  // `.calc-card` entrance plays exactly when this flips true, staying in step with the hero
  // image's drop-in instead of firing immediately on page load.
  const [cardRevealed, setCardRevealed] = useState(() => !animateOnLoad);

  useLayoutEffect(() => {
    if (!animateOnLoad) return;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setPhase('done');
      setCardRevealed(true);
      return;
    }

    const stage = stageRef.current;
    const mediaSlot = mediaSlotRef.current;
    const media = mediaRef.current;
    if (!stage || !mediaSlot || !media) {
      setPhase('done');
      setCardRevealed(true);
      return;
    }

    let cancelled = false;
    const start = () => {
      if (cancelled) return;

      const stageTop = stage.getBoundingClientRect().top;
      const slotTop = mediaSlot.getBoundingClientRect().top;
      stage.style.setProperty('--hero-drop-y', `${slotTop - stageTop}px`);
      setPhase('animating');
    };

    const image = media.querySelector('img');
    const fontsReady =
      document.fonts.status === 'loaded' ? Promise.resolve() : document.fonts.ready;
    const imageReady = new Promise<void>((resolve) => {
      // `complete` is also true for an image that already failed, whose `error`
      // event may have fired before these listeners attach.
      if (!image || image.complete) {
        resolve();
        return;
      }

      image.addEventListener('load', () => resolve(), { once: true });
      image.addEventListener('error', () => resolve(), { once: true });
    }).then(async () => {
      if (!image || typeof image.decode !== 'function') return;
      await image.decode().catch(() => undefined);
    });

    void Promise.all([fontsReady, imageReady]).then(start, start);

    return () => {
      cancelled = true;
    };
  }, [animateOnLoad]);

  useLayoutEffect(() => {
    if (phase !== 'animating') return;

    const media = mediaRef.current;
    if (!media) return;

    const finish = (event: AnimationEvent) => {
      if (event.animationName === 'hero-load-drop') setPhase('settled');
    };
    const fallback = window.setTimeout(
      () => setPhase('settled'),
      MASK_MS + DROP_PAUSE_MS + DROP_MS + 200,
    );
    const cardTimer = window.setTimeout(() => setCardRevealed(true), CARD_LEAD_MS);

    media.addEventListener('animationend', finish);
    return () => {
      media.removeEventListener('animationend', finish);
      window.clearTimeout(fallback);
      window.clearTimeout(cardTimer);
    };
  }, [phase]);

  useLayoutEffect(() => {
    if (phase !== 'settled') return;

    const stage = stageRef.current;
    const mediaSlot = mediaSlotRef.current;
    if (!stage || !mediaSlot) return;

    const syncPosition = () => {
      const stageTop = stage.getBoundingClientRect().top;
      const slotTop = mediaSlot.getBoundingClientRect().top;
      stage.style.setProperty('--hero-drop-y', `${slotTop - stageTop}px`);
    };
    const observer =
      typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(syncPosition);

    observer?.observe(stage);
    window.addEventListener('resize', syncPosition);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', syncPosition);
    };
  }, [phase]);

  const stageStyle = {
    '--hero-mask-ms': `${MASK_MS}ms`,
    '--hero-drop-pause-ms': `${DROP_PAUSE_MS}ms`,
    '--hero-drop-ms': `${DROP_MS}ms`,
    '--hero-content-ms': `${CONTENT_MS}ms`,
    '--hero-content-delay': `${CONTENT_DELAY_MS}ms`,
    '--hero-mask-inset': `${MASK_INSET}%`,
    '--hero-media-lift': `${MEDIA_LIFT}px`,
  } as CSSProperties;
  const loading = phase !== 'done';

  return (
    <Section className="pt-16 md:pt-24 !pb-0">
      <div
        ref={stageRef}
        style={stageStyle}
        className={cn(
          'hero-load',
          phase === 'pending' && 'hero-load--pending',
          phase === 'animating' && 'hero-load--animating',
          phase === 'settled' && 'hero-load--settled',
        )}>
        <div className="hero-load__content">
          <div className="hero-load__copy mx-auto flex max-w-3xl flex-col items-center text-center">
            <div className="flex flex-col items-center gap-6">
              <h1 className="text-[40px] leading-[1.1] font-semibold tracking-[-0.03em] text-balance whitespace-pre-line md:text-title-m">
                {content.title}
              </h1>
              {content.subtitle && (
                <p className="max-w-xl text-body text-grey-600">{content.subtitle}</p>
              )}
            </div>
            <div className="mt-9 flex flex-row flex-nowrap items-center gap-3 whitespace-nowrap md:mt-14">
              <ButtonLink href={content.cta.href} size="md" external={content.cta.external}>
                {content.cta.label}
              </ButtonLink>
              {content.secondaryCta && (
                <ButtonLink
                  href={content.secondaryCta.href}
                  size="md"
                  variant="outline"
                  external={content.secondaryCta.external}>
                  {content.secondaryCta.label}
                </ButtonLink>
              )}
            </div>
            {content.ctaDetail && (
              <p className="mt-6 max-w-xl text-body-s leading-relaxed text-grey-600">
                {content.ctaDetail}
              </p>
            )}
            {content.ctaNote && (
              <p
                className={cn(
                  content.ctaDetail ? 'mt-3' : 'mt-6',
                  'max-w-xl text-body-s leading-relaxed text-grey-600',
                )}>
                {content.ctaNote.text}{' '}
                <ButtonLink
                  href={content.ctaNote.link.href}
                  external={content.ctaNote.link.external}
                  variant="ghost"
                  size="md"
                  className="min-w-0 !p-0 text-[inherit] font-semibold text-ink underline underline-offset-4 hover:bg-transparent hover:text-red-500">
                  {content.ctaNote.link.label}
                </ButtonLink>
              </p>
            )}
          </div>
          <div className="hero-load__stats flow-root">
            {statsSlot ?? (
              <StatsRow stats={stats} className={cn('mt-[104px] md:mt-[124px]', statsClassName)} />
            )}
          </div>
        </div>

        <div
          ref={mediaSlotRef}
          aria-hidden
          className={cn(
            'hero-load__media-slot mt-5 h-[640px] w-full md:h-[800px]',
            !loading && 'hidden',
          )}
        />

        <div
          ref={mediaRef}
          className={cn('hero-load__media h-[640px] w-full md:h-[800px]', !loading && 'mt-5')}>
          <div className="hero-load__frame">
            <SmartImage
              image={content.image}
              loading="eager"
              fetchPriority="high"
              className="size-full"
            />
          </div>

          {showCalculator && cardRevealed && (
            <div className="pointer-events-none absolute inset-0 z-10 grid place-items-center">
              <CurrencyCalculator />
            </div>
          )}
        </div>
      </div>
    </Section>
  );
}
